import { $http } from '@/utils/https'

// team_insight.v1 risk types (ADM-02)
const RISK_TYPE_LABELS = {
	churn       : 'Churn',
	content     : 'Content',
	support     : 'Support',
	expectations: 'Expectations',
	pace        : 'Pace',
	other       : 'Other'
}

const listOrEmpty = value => (Array.isArray(value) ? value : [])

/**
 * Shared read-only dialog showing a single A/B submission
 * (Point A + Point B + AI analysis + team insight). Used by both the
 * course-detail sub-tab and the user-card A/B tab.
 *
 * Team insight (ADM-02): `analysis.team_insight` is the AI v2 insight for
 * the course team (never shown to the student); null or missing (legacy
 * responses, AI v2 off/shadow, team generation failed) hides the block.
 * `analysis.ai_risks` are already the risks for the team (backend rule).
 *
 * Backend: GET admin/ab-forms/{id}?include=course
 */
export default {
	name: 'ab-form-detail-dialog',
	props: {
		value: {
			type   : Boolean,
			default: false
		},
		submissionId: {
			type   : [Number, String],
			default: null
		}
	},
	data () {
		return {
			loading         : false,
			togglingOverride: false,
			detail          : null
		}
	},
	computed: {
		dialog: {
			get () {
				return this.value
			},
			set (val) {
				this.$emit('input', val)
			}
		},
		pointA () {
			return this.detail ? this.detail.point_a : null
		},
		pointB () {
			return this.detail ? this.detail.point_b : null
		},
		analysis () {
			return this.detail ? this.detail.analysis : null
		},
		teamInsight () {
			const insight = this.analysis ? this.analysis.team_insight : null

			if (!insight || typeof insight !== 'object') {
				return null
			}

			return {
				...insight,
				nps_drivers    : listOrEmpty(insight.nps_drivers),
				risks          : listOrEmpty(insight.risks),
				course_feedback: listOrEmpty(insight.course_feedback),
				sales_signal   : insight.sales_signal || null,
				computed       : insight.computed || {}
			}
		},
		salesTopics () {
			return this.teamInsight && this.teamInsight.sales_signal
				? listOrEmpty(this.teamInsight.sales_signal.topics)
				: []
		},
		course () {
			return this.detail && this.detail.course ? this.detail.course.data : null
		},
		email () {
			return this.detail ? this.detail.email : ''
		},
		pointBOverride () {
			return !!(this.detail && this.detail.point_b_override)
		},
		fullName () {
			if (!this.detail) {
				return ''
			}

			return `${this.detail.first_name || ''} ${this.detail.last_name || ''}`.trim()
		}
	},
	watch: {
		value (visible) {
			if (visible && this.submissionId) {
				this.fetchDetail()
			}
		},
		submissionId (id) {
			if (this.value && id) {
				this.fetchDetail()
			}
		}
	},
	methods: {
		async fetchDetail () {
			this.detail = null

			try {
				this.loading = true

				const response = await $http.get(
					`${process.env.VUE_APP_ROOT_COURSE}ab-forms/${this.submissionId}?include=course`
				)

				this.detail = response.data.data
			} catch (e) {
				this.$toasted.error(this.$t('error') || 'Error')
			} finally {
				this.loading = false
			}
		},
		sentimentColor (sentiment) {
			switch (sentiment) {
				case 'positive':
					return 'success'
				case 'negative':
					return 'error'
				case 'neutral':
					return 'grey'
				default:
					return 'grey lighten-1'
			}
		},
		riskTypeLabel (type) {
			return RISK_TYPE_LABELS[type] || RISK_TYPE_LABELS.other
		},
		riskTypeColor (type) {
			return type === 'churn' ? 'error' : 'warning'
		},
		interestColor (interest) {
			switch (interest) {
				case 'high':
					return 'success'
				case 'low':
					return 'grey'
				default:
					return 'grey lighten-1'
			}
		},
		evidenceText (evidence) {
			return listOrEmpty(evidence).join(', ')
		},
		async togglePointBOverride () {
			if (!this.detail) {
				return
			}

			const enabled = !this.pointBOverride

			try {
				this.togglingOverride = true

				await $http.post(
					`${process.env.VUE_APP_ROOT_COURSE}courses/${this.detail.course_id}/users/${this.detail.user_id}/ab-form/point-b-override`,
					{ enabled }
				)

				// Immutable local update; notify parents so their lists refresh.
				this.detail = { ...this.detail, point_b_override: enabled }
				this.$emit('updated')
				this.$toasted.success(this.$t('success') || 'Saved')
			} catch (e) {
				this.$toasted.error(this.$t('error') || 'Error')
			} finally {
				this.togglingOverride = false
			}
		},
		close () {
			this.dialog = false
		}
	}
}
